// src/components/flight-data/AccordionTabs.js
import React, { useState } from 'react';
import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Box,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TdrTab from './TdrTab';
import BrockTab from './BrockTab';
import CombinedTab from './CombinedTab';
import FreightManifestTab from './FreightManifestTab';

/**
 * Component that wraps the tabs in an accordion but keeps the tab headers visible
 */
function AccordionTabs({ tdrData, brockData, freightManifestData, params, inboundETA }) {
  const [expanded, setExpanded] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Auto-expand accordion when tab is clicked
    if (!expanded) {
      setExpanded(true);
    }
  };

  // Render the current tab content
  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return <TdrTab tdrData={tdrData} params={params} inboundETA={inboundETA} />;
      case 1:
        return <BrockTab brockData={brockData} params={params} inboundETA={inboundETA} />;
      case 2:
        return <CombinedTab tdrData={tdrData} brockData={brockData} freightManifestData={freightManifestData} params={params} inboundETA={inboundETA} />;
      case 3:
        return <FreightManifestTab freightManifestData={freightManifestData} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%', mb: 0 }}>
      {/* Custom tabs that remain visible outside the accordion */}
      <Box className="accordion-tabs-bar" sx={{
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'rgba(30, 30, 30, 0.7)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        position: 'relative', 
        zIndex: 1
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: '#aaa',
              '&.Mui-selected': {
                color: tabValue === 0 ? '#F9B612' : tabValue === 1 ? '#FF0000' : tabValue === 3 ? '#4CAF50' : '#ccc',
                fontWeight: 'bold'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: tabValue === 0 ? '#F9B612' : tabValue === 1 ? '#FF0000' : tabValue === 3 ? '#4CAF50' : '#ccc',
            }
          }}
        >
          <Tab label="TDR" />
          <Tab label="Brock" />
          <Tab label="Combined" />
          {freightManifestData && <Tab label="Freight Manifest" />}
        </Tabs>
      </Box>

      {/* Accordion that contains the tab content */}
      <Accordion 
        expanded={expanded} 
        onChange={handleAccordionChange}
        sx={{ 
          backgroundColor: 'rgba(30, 30, 30, 0.7)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
          '&:before': {
            display: 'none'  // Remove the default divider
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
          sx={{ 
            backgroundColor: 'transparent',
            minHeight: '48px',
            '& .MuiAccordionSummary-content': {
              margin: '6px 0'
            }
          }}
        >
          <Typography sx={{ color: '#fff' }}>
            {expanded ? 'Collapse Details' : 'Expand Details'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          {renderTabContent()}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default AccordionTabs;